// Import necessary node module libraries
import { collection, getDocs, getFirestore, query, where } from "firebase/firestore";
import { Fragment, useContext, useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
// Import subcomponents
import Calendar from "components/calendar/Calendar";
import { DashboardContext } from "context/Context";
import Select from "react-select";
import RivSummary from "./RivSummary";

const RivalCalendar = () => {
  const db = getFirestore();

  const { user } = useContext(DashboardContext);

  const [data, setData] = useState([]);
  const [dataRival, setDataRival] = useState([]);
  const [businessLocations, setBusinessLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [change, setChange] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());

  const [magnaProfits, setMagnaProfits] = useState([]);
  const [premiumProfits, setPremiumProfits] = useState([]);
  const [magnaLiters, setMagnaLiters] = useState([]);
  const [premiumLiters, setPremiumLiters] = useState([]);
  const [magnaSales, setMagnaSales] = useState([]);
  const [premiumSales, setPremiumSales] = useState([]);
  const [dateSArray, setDateSArray] = useState([]);
  const [magnaPrices, setMagnaPrices] = useState([]);
  const [premiumPrices, setPremiumPrices] = useState([]);
  const [magnaCosts, setMagnaCosts] = useState([]);
  const [premiumCosts, setPremiumCosts] = useState([]);
  const [rivalMagnaPrices, setRivalMagnaPrices] = useState([]);
  const [rivalPremiumPrices, setRivalPremiumPrices] = useState([]);
  const [rivalAlias, setRivalAlias] = useState("");

  // Fetch data from Firestore
  const fetchData = async (collectionName, startOfMonth, endOfMonth) => {
    const colRef = collection(db, "companies", user.companyId, collectionName);
    const q = query(colRef, where("date", ">=", startOfMonth), where("date", "<=", endOfMonth));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => doc.data());
  };

  // Fetch locations from Firestore
  const fetchLocations = async () => {
    const colRef = collection(db, "companies", user.companyId, "locations");
    const snapshot = await getDocs(colRef);
    return snapshot.docs.map((doc) => ({ locId: doc.id, ...doc.data() }));
  };

  const loadData = async () => {
    setLoading(true);
    const startOfMonth = new Date(new Date().getFullYear(), currentMonth, 1);
    const endOfMonth = new Date(new Date().getFullYear(), currentMonth + 1, 0);
    startOfMonth.setHours(0, 0, 0, 0);
    endOfMonth.setHours(23, 59, 59, 999);

    const [data, dataRival, locations] = await Promise.all([
      fetchData("dayInformation", startOfMonth, endOfMonth),
      fetchData("rivalPrices", startOfMonth, endOfMonth),
      fetchLocations(),
    ]);

    setData(data);
    console.log("rival data", dataRival);
    setDataRival(dataRival);
    setBusinessLocations(
      locations.map((location) => ({
        value: location,
        label: location.name,
      }))
    );
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [currentMonth, change]);

  useEffect(() => {
    const rivalId = window.location.pathname.split("/")[3];

    const sortedRivalData = [...dataRival].sort(
      (a, b) => (a.date?.seconds || 0) - (b.date?.seconds || 0)
    );

    const rivalDates = [];
    const rivalMagnaOnly = [];
    const rivalPremiumOnly = [];
    let alias = "";

    const rivalDataMap = sortedRivalData.reduce((acc, rivalEntry) => {
      if (!rivalEntry?.date?.seconds) return acc;

      const rivalDate = new Date(rivalEntry.date.seconds * 1000).toLocaleDateString("es-MX");
      acc[rivalDate] = rivalEntry;

      if (rivalEntry?.[rivalId]) {
        rivalDates.push(rivalDate);
        rivalMagnaOnly.push(rivalEntry[rivalId].magnaPrice ?? null);
        rivalPremiumOnly.push(rivalEntry[rivalId].premiumPrice ?? null);
        if (!alias && rivalEntry[rivalId].alias) alias = rivalEntry[rivalId].alias;
      }

      return acc;
    }, {});

    const aliasValue = alias || "Rival";
    const setRivalOnlySeries = () => {
      const safeLength = rivalDates.length || 1;
      const emptySeries = Array(safeLength).fill(null);
      const xAxis = rivalDates.length ? rivalDates : [""];
      const rivalMagnaSeries = rivalMagnaOnly.length ? rivalMagnaOnly : emptySeries;
      const rivalPremiumSeries = rivalPremiumOnly.length ? rivalPremiumOnly : emptySeries;

      setDateSArray(xAxis);
      setMagnaProfits(emptySeries);
      setPremiumProfits(emptySeries);
      setMagnaLiters(emptySeries);
      setPremiumLiters(emptySeries);
      setMagnaSales(emptySeries);
      setPremiumSales(emptySeries);
      setMagnaPrices(emptySeries);
      setPremiumPrices(emptySeries);
      setMagnaCosts(emptySeries);
      setPremiumCosts(emptySeries);
      setRivalMagnaPrices(rivalMagnaSeries);
      setRivalPremiumPrices(rivalPremiumSeries);
    };

    setRivalAlias(aliasValue);

    const locationId = selectedLocation?.value?.locId;
    if (!locationId || data.length === 0) {
      setRivalOnlySeries();
      return;
    }

    const mProfits = [];
    const pProfits = [];
    const mLiters = [];
    const pLiters = [];
    const mSales = [];
    const pSales = [];
    const mPrices = [];
    const pPrices = [];
    const mCosts = [];
    const pCosts = [];
    const rivalMagna = [];
    const rivalPremium = [];
    const dsArray = [];

    data.forEach((entry) => {
      const entryDate = new Date(entry.date.seconds * 1000).toLocaleDateString("es-MX");
      const locationData = entry[locationId];
      const rivalEntry = rivalDataMap[entryDate]?.[rivalId];

      const hasMagna = locationData?.magnaSalesIncome !== undefined;
      const hasPremium = locationData?.premiumSalesIncome !== undefined;

      if (!hasMagna && !hasPremium) return;

      dsArray.push(entryDate);

      mProfits.push(
        hasMagna
          ? locationData.magnaSalesIncome - locationData.magnaLitersSold * locationData.magnaCost
          : null
      );
      mLiters.push(hasMagna ? locationData.magnaLitersSold : null);
      mSales.push(hasMagna ? locationData.magnaSalesIncome : null);
      mPrices.push(hasMagna ? locationData.magnaPrice : null);
      mCosts.push(hasMagna ? locationData.magnaCost : null);

      pProfits.push(
        hasPremium
          ? locationData.premiumSalesIncome -
              locationData.premiumLitersSold * locationData.premiumCost
          : null
      );
      pLiters.push(hasPremium ? locationData.premiumLitersSold : null);
      pSales.push(hasPremium ? locationData.premiumSalesIncome : null);
      pPrices.push(hasPremium ? locationData.premiumPrice : null);
      pCosts.push(hasPremium ? locationData.premiumCost : null);

      rivalMagna.push(rivalEntry?.magnaPrice ?? null);
      rivalPremium.push(rivalEntry?.premiumPrice ?? null);
    });

    if (dsArray.length === 0) {
      setRivalOnlySeries();
      return;
    }

    setMagnaProfits(mProfits);
    setPremiumProfits(pProfits);
    setMagnaLiters(mLiters);
    setPremiumLiters(pLiters);
    setMagnaSales(mSales);
    setPremiumSales(pSales);
    setDateSArray(dsArray);
    setMagnaPrices(mPrices);
    setPremiumPrices(pPrices);
    setMagnaCosts(mCosts);
    setPremiumCosts(pCosts);
    setRivalMagnaPrices(rivalMagna);
    setRivalPremiumPrices(rivalPremium);
  }, [selectedLocation, data, dataRival]);

  return (
    <Fragment>
      <Card className="mb-4">
        <div className="w-100 p-3">
          <div className="d-flex align-items-baseline justify-content-end">
            {!loading && businessLocations.length > 0 && (
              <div>
                <h5 className="mb-0 card-title me-4">Selecciona la ubicación</h5>
                <Select
                  className="react-select-container"
                  classNamePrefix="react-select"
                  placeholder="Selecciona"
                  value={selectedLocation}
                  options={businessLocations}
                  onChange={(e) => setSelectedLocation(e)}
                />
              </div>
            )}
          </div>
        </div>

        <Calendar
          data={data}
          selectedLocation={selectedLocation?.value}
          callbackFn={() => setChange(!change)}
          currentMonthFn={(res) => setCurrentMonth(res)}
        />
      </Card>

      <RivSummary
        ventaData={magnaSales}
        litrosData={magnaLiters}
        utilidadData={magnaProfits}
        xAxisData={dateSArray}
        priceData={magnaPrices}
        costData={magnaCosts}
        rivalPriceData={rivalMagnaPrices}
        title={`Regular - ${rivalAlias}`}
      />

      <br />

      <RivSummary
        ventaData={premiumSales}
        litrosData={premiumLiters}
        utilidadData={premiumProfits}
        xAxisData={dateSArray}
        priceData={premiumPrices}
        costData={premiumCosts}
        rivalPriceData={rivalPremiumPrices}
        title={`Premium - ${rivalAlias}`}
      />

      <Row>
        <Col lg={6} className="mb-4">
          {/* Placeholder */}
        </Col>
        <Col lg={6} className="mb-4">
          {/* Placeholder */}
        </Col>
      </Row>
    </Fragment>
  );
};

export default RivalCalendar;
